import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthConfig } from '../utils/auth';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowRight, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const flooringOptions = [
  { 
    value: 'engineered_hardwood', 
    label: 'Engineered Hardwood',
    colors: [
      { value: 'walnut', label: 'Walnut' },
      { value: 'smoked_oak', label: 'Smoked Oak' },
      { value: 'american_birch', label: 'American Birch' },
      { value: 'natural_oak', label: 'Natural Oak' }
    ]
  },
  { 
    value: 'lvp', 
    label: 'LVP (Luxury Vinyl Plank)',
    colors: [
      { value: 'black_walnut', label: 'Black Walnut' },
      { value: 'teak', label: 'Teak' },
      { value: 'grey_oak', label: 'Grey Oak' },
      { value: 'natural_oak', label: 'Natural Oak' }
    ]
  }
];

function Pledge() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [flooringType, setFlooringType] = useState('');
  const [color, setColor] = useState('');
  const [sqft, setSqft] = useState('1500');
  const [submitting, setSubmitting] = useState(false);

  const selectedFlooring = flooringOptions.find(f => f.value === flooringType);
  const availableColors = selectedFlooring?.colors || [];

  const handleFlooringChange = (value) => {
    setFlooringType(value);
    setColor('');
    setStep(2);
  };

  const handleColorChange = (value) => {
    setColor(value);
    setStep(3);
  };

  const getPriceRange = () => {
    if (flooringType === 'lvp') {
      return '$0.67–$2.99';
    } else if (flooringType === 'engineered_hardwood') {
      return '$2.39–$6.99';
    }
    return '$0.67–$6.99';
  };

  const handleSubmit = async () => {
    if (!flooringType || !color || !sqft || parseInt(sqft) < 1500) {
      toast.error('Please complete all fields (minimum 1500 sqft)');
      return;
    }

    setSubmitting(true);
    try {
      // Check if user is authenticated
      await axios.get(`${BACKEND_URL}/api/auth/me`, getAuthConfig());
      
      // Store pledge data temporarily
      sessionStorage.setItem('pledge_data', JSON.stringify({
        flooring_type: flooringType,
        color: color,
        sqft: parseInt(sqft)
      }));
      
      // Navigate to confirmation
      navigate('/pledge/confirmation');
    } catch (error) {
      if (error.response?.status === 401) {
        // Redirect to login
        const redirectUrl = window.location.origin + '/pledge';
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      } else {
        toast.error('Failed to submit pledge');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <Navbar />
      
      <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8" data-testid="pledge-page">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 animate-fade-in">
            <div className="inline-block mb-4">
              <span className="px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold">
                Container #20491
              </span>
            </div>
            <h1 className="font-bold text-4xl md:text-6xl tracking-tight leading-tight mb-6" data-testid="pledge-title">
              Pledge Material
            </h1>
            <p className="text-lg text-gray-600">
              Join this container and lock in wholesale pricing
            </p>
          </div>

          {/* Form */}
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 border border-gray-100">
            {/* Step 1: Flooring Type */}
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                  step >= 1 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {step > 1 ? <Check className="w-5 h-5" /> : '1'}
                </div>
                <label className="text-lg font-bold">Select Flooring Type</label>
              </div>
              <Select value={flooringType} onValueChange={handleFlooringChange}>
                <SelectTrigger className="h-14 text-lg rounded-2xl border-2" data-testid="flooring-type-select">
                  <SelectValue placeholder="Choose flooring type..." />
                </SelectTrigger>
                <SelectContent>
                  {flooringOptions.map(option => (
                    <SelectItem key={option.value} value={option.value} className="text-lg">
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Step 2: Color */}
            {step >= 2 && (
              <div className="mb-8 animate-fade-in">
                <div className="flex items-center mb-4">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                    step >= 2 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {step > 2 ? <Check className="w-5 h-5" /> : '2'}
                  </div>
                  <label className="text-lg font-bold">Select Color</label>
                </div>
                <Select value={color} onValueChange={handleColorChange}>
                  <SelectTrigger className="h-14 text-lg rounded-2xl border-2" data-testid="color-select">
                    <SelectValue placeholder="Choose color..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableColors.map(colorOption => (
                      <SelectItem key={colorOption.value} value={colorOption.value} className="text-lg">
                        {colorOption.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Step 3: Square Feet */}
            {step >= 3 && (
              <div className="mb-8 animate-fade-in">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center mr-3">
                    3
                  </div>
                  <label className="text-lg font-bold">How many square feet?</label>
                </div>
                <Input
                  type="number"
                  min="1500"
                  value={sqft}
                  onChange={(e) => setSqft(e.target.value)}
                  placeholder="1,500 SQFT minimum"
                  className="h-14 text-lg rounded-2xl border-2"
                  data-testid="sqft-input"
                />
                <p className="text-sm text-gray-600 mt-2">
                  Estimated Cost: <span className="font-semibold">{getPriceRange()} per SQFT</span>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  (Pricing not charged now — pledge only. Elite members get best rates)
                </p>
              </div>
            )}

            {/* Submit Button */}
            {step >= 3 && (
              <div className="animate-fade-in">
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full h-14 text-lg font-bold rounded-2xl bg-gradient-to-r from-primary to-accent hover:shadow-2xl transition-all"
                  data-testid="confirm-pledge-button"
                >
                  {submitting ? 'Processing...' : 'Confirm My Pledge'}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            )}
          </div>

          {/* Info Box */}
          <div className="mt-8 p-6 bg-blue-50 rounded-2xl border border-blue-100">
            <h3 className="font-bold text-lg mb-2">What happens next?</h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start">
                <Check className="w-4 h-4 text-primary mr-2 mt-0.5 flex-shrink-0" />
                <span>You'll receive confirmation once the container is ready to ship</span>
              </li>
              <li className="flex items-start">
                <Check className="w-4 h-4 text-primary mr-2 mt-0.5 flex-shrink-0" />
                <span>A 10% deposit secures your place and locks your pricing</span>
              </li>
              <li className="flex items-start">
                <Check className="w-4 h-4 text-primary mr-2 mt-0.5 flex-shrink-0" />
                <span>Balance due when container is nearly full and ready for shipment</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Pledge;
